import API_CONFIG from '../config/api.config';
import React from 'react';
import { showToast } from '../config/toast.config';
import notificationSound from '../assets/audio/notification.mp3';
import { claimTaskNotification } from '../utils/taskNotificationClaims';

interface TaskNotification {
  idTask: number;
  idProduct: number;
  barcode: string;
  totalActiveTasks: number;
}

// Open task counts for the bell, split into the two lanes:
// urgent (red, priority high) and daily (blue, normal/low). total = daily + urgent.
export interface TaskBellCounts {
  total: number;
  daily: number;
  urgent: number;
}

interface QuoteChangeNotification {
  quote_id: string;
  action: string;
  details: Record<string, any>;
  actor: string;
}

interface KranQuoteCountsNotification {
  counts: Record<string, number>;
  actor?: string;
}

interface TaskChangeNotification {
  action: string;
  actor: string;
  details: any;
}

interface TranscodeUpdateNotification {
  module: 'vehicles' | 'line_items';
  record_id: number | string;
  entry_id: string;
  status: 'ready' | 'failed';
  url?: string;
  poster_url?: string;
  small_url?: string;
}

// One CRM mutation (a crm_activities row) broadcast by the backend notifier
// loop so open record pages can refetch + highlight what changed.
interface RecordChangeNotification {
  activity_id: number;
  entity_type: 'quote' | 'sale' | 'crm_return' | 'line_item';
  entity_id: number;
  entity_hash?: string | null; // Q-…/S-…/R-… form of entity_id (null for line_item)
  action: string; // create | update | status_change | comment | delete | system
  message?: string | null;
  changes: Record<string, { from: unknown; to: unknown }>;
  payload?: Record<string, any>;
  line_item_ids: number[];
  actor_user_id?: number | null;
  actor_username?: string | null;
  at?: string | null;
}

// Ephemeral comment/edit-drawer presence — NOT a mutation. Server
// stamps user_id/username from the verified token; never persisted. `target`
// is an opaque scope string, e.g. "li:123" (line item) or "quote:Q-…" (record).
interface RecordTypingNotification {
  target: string;
  composing: boolean;
  editing?: boolean;
  session_id?: string;
  user_id: number;
  username: string;
  at?: string | null;
}

interface WebSocketMessage {
  type: string;
  task?: TaskNotification; // This can be deprecated or kept for other features
  data?: TaskChangeNotification | QuoteChangeNotification | KranQuoteCountsNotification | TranscodeUpdateNotification;
}

// Resolved fresh on EVERY connect attempt, never captured once: the desktop
// access token lives 15 minutes (60s on a kiosk terminal), so a string handed
// in at login is already dead by the first reconnect. May be async so the
// caller can mint a new token before we dial (see App.jsx).
type TokenProvider = () => string | null | Promise<string | null>;

class WebSocketService {
  private static instance: WebSocketService;
  private ws: WebSocket | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private listeners: Set<(task: TaskNotification) => void> = new Set();
  private taskCountListeners: Set<(counts: TaskBellCounts) => void> = new Set();
  private quoteChangeListeners: Set<(notification: QuoteChangeNotification) => void> = new Set();
  private kranQuoteCountListeners: Set<(counts: Record<string, number>) => void> = new Set();
  private transcodeUpdateListeners: Set<(notification: TranscodeUpdateNotification) => void> = new Set();
  private recordChangeListeners: Set<(notification: RecordChangeNotification) => void> = new Set();
  private recordTypingListeners: Set<(notification: RecordTypingNotification) => void> = new Set();
  private reconnectListeners: Set<() => void> = new Set();
  private whatsappUpdateListeners: Set<(data: any) => void> = new Set();
  private whatsappSessionStatusListeners: Set<(data: any) => void> = new Set();
  private isConnecting: boolean = false;
  private connectionState: 'disconnected' | 'connecting' | 'connected' | 'error' = 'disconnected';
  private tokenProvider: TokenProvider | null = null;
  // Bumped by disconnect(); a connect attempt that was awaiting its token when
  // the user logged out must not go on to open a socket with the old identity.
  private sessionEpoch = 0;
  // Reconnect-ness must NOT be inferred from reconnectAttempts: the instant
  // wake-path retry (registerWakeListeners) resets the counter before
  // connect(), which would silently skip the reconnect resync listeners.
  private hadConnectedBefore = false;

  private notificationUserId: number | string | null = null;

  private constructor() {}

  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  getConnectionState(): string {
    return this.connectionState;
  }

  async connect(tokenProvider: TokenProvider | string, userId: number | string | null = this.notificationUserId) {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    // Accept a bare string for older callers, but normalize to a getter.
    this.tokenProvider = typeof tokenProvider === 'function' ? tokenProvider : () => tokenProvider;
    this.notificationUserId = userId;
    this.registerWakeListeners();
    this.isConnecting = true;
    this.connectionState = 'connecting';

    const epoch = this.sessionEpoch;
    let token: string | null = null;
    try {
      // Bounded: the provider may hit the network (token refresh) and a request
      // that hangs would leave isConnecting stuck true, which silently kills
      // every future reconnect for the life of the tab.
      token = await Promise.race([
        Promise.resolve(this.tokenProvider()),
        new Promise<null>((_, reject) =>
          setTimeout(() => reject(new Error('token provider timed out')), 10000)
        ),
      ]);
    } catch (err) {
      console.error('WebSocket token provider failed:', err);
      this.isConnecting = false;
      this.connectionState = 'error';
      if (epoch === this.sessionEpoch) this.handleReconnect();
      return;
    }
    if (epoch !== this.sessionEpoch) {
      // disconnect() (logout / unmount) landed while we were minting a token.
      this.isConnecting = false;
      return;
    }
    if (!token) {
      // Provider answered, and the answer is "there is no session". Do NOT
      // schedule a retry: the backoff would just burn handshakes against a
      // session that is gone. The wake listeners and 'auth:tokenRefreshed'
      // bring us back the moment there is a token again.
      console.warn('WebSocket connect skipped: no access token available');
      this.isConnecting = false;
      this.connectionState = 'disconnected';
      return;
    }

    try {
      const baseWsUrl = API_CONFIG.WS_URL || '';
      const normalizedBase = baseWsUrl.endsWith('/') ? baseWsUrl.slice(0, -1) : baseWsUrl;
      const wsEndpoint = normalizedBase.endsWith('/ws') ? '/notifications' : '/ws/notifications';
      const wsUrl = `${normalizedBase}${wsEndpoint}?token=${token}`;
      
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected successfully');
        const wasReconnect = this.hadConnectedBefore;
        this.hadConnectedBefore = true;
        this.reconnectAttempts = 0;
        this.isConnecting = false;
        this.connectionState = 'connected';
        // On connect, consumers should fetch fresh counts via REST.
        // After a RE-connect, record_change events may have been missed while
        // offline, so open record pages must refetch to resync.
        if (wasReconnect) {
          this.reconnectListeners.forEach(listener => {
            try { listener(); } catch {}
          });
        }
      };

      this.ws.onmessage = (event) => {
        // Ignore queued frames from a socket belonging to a previous login.
        if (epoch !== this.sessionEpoch) return;
        // Dev-only: these fire for EVERY broadcast on EVERY tab. In prod, at
        // 150 tabs × every record_change that is a steady stream of console
        // writes + retained objects (memory growth in long-lived tabs).
        if (import.meta.env.DEV) {
          console.log('%c[WS] Raw message received:', 'color: gray', event.data);
        }
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          if (import.meta.env.DEV) {
            console.log('%c[WS] Parsed message:', 'color: blue', message);
          }
          
          if (message.type === 'task_change' && message.data && 'action' in message.data) {
            console.log('%c[WS] Processing task_change message...', 'color: green');
            const taskChange = message.data as TaskChangeNotification;
            const taskData = taskChange.details;
            console.log('%c[WS] Extracted taskData:', 'color: green', taskData);
            console.log('%c[WS] Using CRM rich toast for task_change', 'color: orange');
            
            // Re-shape the data to fit the existing showNotification function if possible
            const notificationData: TaskNotification = {
              idTask: taskData.id,
              barcode: taskData.line_items?.map(li => li.sku?.barcode).filter(Boolean).join(', ') || 'N/A',
              idProduct: taskData.line_items?.[0]?.product_id || 0,
              totalActiveTasks: 0, // This might need an update if the backend sends a new total
            };
            console.log('%c[WS] Created notificationData:', 'color: green', notificationData);

            // Show toast/sound ONLY for new task creation; updates just sync counts
            if (taskChange.action === 'task_created') {
              void this.notifyNewTaskOnce('crm', taskData?.id, () => {
                const body = this.buildCrmTaskToastText(taskData);
                const content = this.buildCrmTaskToastElement(taskData);
                const notificationTitle = `Task #${taskData?.id ?? ''} - ${taskChange?.action ?? 'Actualizare'}`.trim();
                this.showBrowserNotification(notificationTitle, body);
                showToast.info(content);
                this.playNotificationSound();
              });
            }
            this.notifyTaskListeners(notificationData);
            console.log('%c[WS] Finished processing task_change message.', 'color: green');

            // If server provided authoritative counts, propagate them to badge listeners.
            // New shape carries daily/urgent lanes; open_task_count remains the total.
            const data = message.data as any;
            const openTaskCount = data.open_task_count as number | undefined;
            if (typeof openTaskCount === 'number') {
              const urgent = typeof data.urgent_task_count === 'number' ? data.urgent_task_count : 0;
              const daily = typeof data.daily_task_count === 'number' ? data.daily_task_count : openTaskCount - urgent;
              this.notifyTaskCountListeners({ total: openTaskCount, daily, urgent });
            }

          } else if (message.type === 'new_task' && message.task) {
            void this.notifyNewTaskOnce('legacy', message.task.idTask, () => {
              showToast.info('Task Nou');
              this.playNotificationSound();
            });
            this.notifyTaskCountListeners({ total: message.task.totalActiveTasks, daily: message.task.totalActiveTasks, urgent: 0 });
            this.notifyTaskListeners(message.task);
          } else if (message.type === 'quote_change' && message.data) {
            this.handleQuoteChange(message.data as QuoteChangeNotification);
          } else if (message.type === 'transcode_update' && message.data) {
            this.notifyTranscodeUpdateListeners(message.data as TranscodeUpdateNotification);
          } else if (message.type === 'record_change' && message.data) {
            this.recordChangeListeners.forEach(listener => {
              try { listener(message.data as RecordChangeNotification); } catch (err) {
                console.error('record_change listener failed:', err);
              }
            });
          } else if (message.type === 'record_typing' && message.data) {
            this.recordTypingListeners.forEach(listener => {
              try { listener(message.data as RecordTypingNotification); } catch (err) {
                console.error('record_typing listener failed:', err);
              }
            });
          } else if (message.type === 'kran_quote_counts' && message.data) {
            const counts = (message.data as KranQuoteCountsNotification).counts;
            if (counts && typeof counts === 'object') {
              this.notifyKranQuoteCountListeners(counts);
            }
          } else if (message.type === 'whatsapp_update' && message.data) {
            this.whatsappUpdateListeners.forEach(listener => {
              try { listener(message.data); } catch {}
            });
          } else if (message.type === 'whatsapp_session_status' && message.data) {
            this.whatsappSessionStatusListeners.forEach(listener => {
              try { listener(message.data); } catch {}
            });
          }
        } catch (error) {
          console.error('Error processing WebSocket message:', error);
          showToast.error(error as any);
        }
      };

      this.ws.onclose = (event) => {
        this.isConnecting = false;
        this.connectionState = 'disconnected';
        
        // Log detailed close information
        const reason = event.reason || 'No reason provided';
        const code = event.code;
        console.log(`WebSocket closed - Code: ${code}, Reason: ${reason}`);
        
        // Handle specific close codes
        switch (code) {
          case 1000:
            // Normal closure
            break;
          case 1006:
            console.error('WebSocket connection abnormally closed');
            break;
          case 1015:
            console.error('TLS handshake failed');
            this.showError('Secure connection failed. Please check your connection and try again.');
            break;
          default:
            if (code >= 1002 && code <= 1015) {
              console.error(`Protocol error (${code}): ${reason}`);
            }
        }

        if (code === 4001) {
          // Server rejected the token, and the provider had already refreshed
          // it before we dialled — so the session itself is gone and retrying
          // only burns handshakes. Wait for 'auth:tokenRefreshed' or a wake
          // event instead. NOTE: this code only reaches us because the backend
          // accepts before closing; closing a never-accepted socket makes
          // uvicorn reject the handshake with HTTP 403 and the browser reports
          // a bare 1006. Never rely on 4001 alone to detect an auth problem —
          // the real defence is that every retry re-mints via tokenProvider().
          console.error('WebSocket auth rejected (4001); waiting for a fresh token');
          return;
        }
        this.handleReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.connectionState = 'error';
        this.isConnecting = false;
        
        // Let onclose handle reconnection
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.close();
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      showToast.error(error as any);
      this.isConnecting = false;
      this.connectionState = 'error';
      this.handleReconnect();
    }
  }

  private showError(message: string) {
    showToast.error(message);
  }

  private handleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    // Never give up: realtime record updates are load-bearing for data
    // freshness, and a backend deploy or laptop sleep easily outlasts a
    // finite retry budget. Exponential backoff capped at 30s, forever;
    // an 'online' / tab-visible signal retries immediately (see connect()).
    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      // Re-read the token at fire time, not at schedule time: after a 30s
      // backoff (or a laptop that slept through the TTL) the token captured
      // when the socket died is exactly the one the server will refuse.
      if (this.tokenProvider) this.connect(this.tokenProvider);
    }, delay);
  }

  // Retry instantly when connectivity plausibly returned (network back or
  // user came back to the tab), instead of waiting out the 30s backoff.
  private wakeListenersRegistered = false;
  private registerWakeListeners() {
    if (this.wakeListenersRegistered || typeof window === 'undefined') return;
    this.wakeListenersRegistered = true;
    const retryNow = () => {
      if (!this.tokenProvider) return;
      if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) return;
      if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
      this.reconnectAttempts = 0;
      this.connect(this.tokenProvider);
    };
    window.addEventListener('online', retryNow);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') retryNow();
    });
    // A refreshed access token is the one signal that un-blocks a socket the
    // server refused for auth: nothing about the network changed, so neither
    // 'online' nor 'visibilitychange' would ever fire.
    window.addEventListener('auth:tokenRefreshed', retryNow);
  }

  private async notifyNewTaskOnce(taskType: 'crm' | 'legacy', taskId: number, notify: () => void) {
    const epoch = this.sessionEpoch;
    const userId = this.notificationUserId;
    // Give the visible tab a chance to own the alert. Hidden tabs may still
    // notify when the user is in another application; they never queue a
    // second toast for their next visibilitychange.
    if (document.visibilityState !== 'visible') {
      await new Promise(resolve => setTimeout(resolve, 250));
    }
    if (epoch !== this.sessionEpoch) return;
    const claimed = await claimTaskNotification({ userId, taskType, taskId });
    if (!claimed || epoch !== this.sessionEpoch) return;
    try {
      notify();
    } catch (error) {
      console.warn('Could not show task notification:', error);
    }
  }

  private playNotificationSound() {
    const audio = new Audio(notificationSound);
    audio.play().catch(error => {
      console.error("Failed to play notification sound:", error);
      showToast.error(error);
    });
  }

  // Android Chrome (and any browser without a desktop notification surface)
  // forbids `new Notification(...)`: it throws "Illegal constructor. Use
  // ServiceWorkerRegistration.showNotification() instead". Try the service
  // worker first, fall back to the constructor on desktop, and stay silent if
  // neither works - the in-app toast + sound are the real notification, so a
  // failure here must never surface as an error toast to the operator.
  private showBrowserNotification(title: string, body: string) {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    const options: NotificationOptions = {
      body,
      icon: '/favicon/android-chrome-192x192.png'
    };

    const viaConstructor = () => {
      try {
        new Notification(title, options);
      } catch (err) {
        console.warn('Browser notification not available:', err);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration()
        .then(registration => {
          if (registration) return registration.showNotification(title, options);
          viaConstructor();
        })
        .catch(() => viaConstructor());
      return;
    }

    viaConstructor();
  }

  // Build human-friendly CRM task toast text from rich task details
  private buildCrmTaskToastText(details: any): string {
    // Keep it simple: only the title, no counts or extra lines
    return 'Task Nou';
  }

  // Build a minimal React element toast: title only (as a link)
  private buildCrmTaskToastElement(details: any): React.ReactNode {
    const taskId = details?.id;
    const taskUrl = `/crm/tasks/${taskId}`;
    const headerText = 'Task Nou';

    return (
      React.createElement('div', null,
        React.createElement('a', { href: taskUrl, style: { fontWeight: 600, color: '#0ea5e9', textDecoration: 'underline' } }, headerText)
      )
    );
  }

  private handleQuoteChange(notification: QuoteChangeNotification) {
    console.log('Quote change notification:', notification);
    this.notifyQuoteChangeListeners(notification);
  }

  addListener(callback: (task: TaskNotification) => void) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  addTaskCountListener(callback: (counts: TaskBellCounts) => void) {
    this.taskCountListeners.add(callback);
    return () => this.taskCountListeners.delete(callback);
  }

  addQuoteChangeListener(callback: (notification: QuoteChangeNotification) => void) {
    this.quoteChangeListeners.add(callback);
    return () => this.quoteChangeListeners.delete(callback);
  }

  addKranQuoteCountListener(callback: (counts: Record<string, number>) => void) {
    this.kranQuoteCountListeners.add(callback);
    return () => this.kranQuoteCountListeners.delete(callback);
  }

  addTranscodeUpdateListener(callback: (notification: TranscodeUpdateNotification) => void) {
    this.transcodeUpdateListeners.add(callback);
    return () => this.transcodeUpdateListeners.delete(callback);
  }

  addRecordChangeListener(callback: (notification: RecordChangeNotification) => void) {
    this.recordChangeListeners.add(callback);
    return () => this.recordChangeListeners.delete(callback);
  }

  addRecordTypingListener(callback: (notification: RecordTypingNotification) => void) {
    this.recordTypingListeners.add(callback);
    return () => this.recordTypingListeners.delete(callback);
  }

  // The ONLY client→server write. Ephemeral typing presence; dropped silently
  // if the socket isn't open (never queued — a missed heartbeat just lets the
  // peer's dots expire, which is the desired behavior).
  sendTyping(payload: { target: string; composing: boolean; editing?: boolean }) {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify({ type: 'record_typing', data: payload }));
    } catch (err) {
      if (import.meta.env.DEV) console.warn('sendTyping failed:', err);
    }
  }

  addReconnectListener(callback: () => void) {
    this.reconnectListeners.add(callback);
    return () => this.reconnectListeners.delete(callback);
  }

  addWhatsAppUpdateListener(callback: (data: any) => void) {
    this.whatsappUpdateListeners.add(callback);
    return () => this.whatsappUpdateListeners.delete(callback);
  }

  addWhatsAppSessionStatusListener(callback: (data: any) => void) {
    this.whatsappSessionStatusListeners.add(callback);
    return () => this.whatsappSessionStatusListeners.delete(callback);
  }

  private notifyTaskListeners(task: TaskNotification) {
    this.listeners.forEach(listener => listener(task));
  }

  private notifyTaskCountListeners(counts: TaskBellCounts) {
    this.taskCountListeners.forEach(listener => listener(counts));
  }

  private notifyQuoteChangeListeners(notification: QuoteChangeNotification) {
    this.quoteChangeListeners.forEach(listener => listener(notification));
  }

  private notifyKranQuoteCountListeners(counts: Record<string, number>) {
    this.kranQuoteCountListeners.forEach(listener => listener(counts));
  }

  private notifyTranscodeUpdateListeners(notification: TranscodeUpdateNotification) {
    this.transcodeUpdateListeners.forEach(listener => listener(notification));
  }

  disconnect() {
    this.sessionEpoch++;
    this.notificationUserId = null;
    this.tokenProvider = null; // stops the wake listeners from redialling after logout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      this.ws.close(1000, 'Disconnected by user');
      this.ws = null;
    }
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    this.connectionState = 'disconnected';
    this.hadConnectedBefore = false; // a fresh post-logout connect is not a "reconnect"
  }
}

export type { QuoteChangeNotification, TranscodeUpdateNotification, RecordChangeNotification, RecordTypingNotification };
export default WebSocketService.getInstance();
